import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChildren,
} from '@angular/core';
import { AssetTimeseriesProperty } from '@archimed/models';
import {
  ButtonComponent,
  IconButtonComponent,
  IconComponent,
  SegmentedControlComponent,
  SegmentedControlOptionComponent,
} from '@totalenergiescode/tds-angular';
import { firstValueFrom } from 'rxjs';
import { Asset, AssetRelationship } from '../../../domain/models/asset.model';
import { AssetInstancesApiAdapter } from '../../../infrastructure/adapters/asset-instances-api.adapter';
import { AssetIconPipe } from '../../pipes/asset-icon.pipe';
import { TimeseriesTabComponent } from '../timeseries-tab/timeseries-tab.component';
import { TreeNode } from '../tree-view/tree-node.model';
import { InlineEditFieldComponent } from './inline-edit-field/inline-edit-field.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DatePipe,
    IconComponent,
    IconButtonComponent,
    ButtonComponent,
    SegmentedControlComponent,
    SegmentedControlOptionComponent,
    AssetIconPipe,
    TimeseriesTabComponent,
    InlineEditFieldComponent,
  ],
  selector: 'lib-asset-details',
  standalone: true,
  styleUrl: './asset-details.component.scss',
  templateUrl: './asset-details.component.html',
})
export class AssetDetailsComponent {
  private readonly apiAdapter = inject(AssetInstancesApiAdapter);

  readonly asset = input.required<Asset>();
  readonly timeseriesProperties = input<AssetTimeseriesProperty[]>([]);

  readonly close = output<void>();
  readonly navigateToAsset = output<string>();
  readonly propertyUpdated = output<{
    propertyName: string;
    value: unknown;
  }>();

  readonly technicalDetailsExpanded = signal(false);
  readonly structuredPropertiesExpanded = signal(false);
  readonly activeTabIndex = signal(0);
  readonly hasTimeseries = computed(
    () => this.timeseriesProperties().length > 0,
  );

  readonly editFields = viewChildren(InlineEditFieldComponent);

  // Local overrides for properties that have been edited (triggers reactivity)
  private readonly propertyOverrides = signal<Record<string, unknown>>({});

  private readonly _resetOnAssetChange = effect(() => {
    this.asset();
    this.activeTabIndex.set(0);
    // Clear overrides when asset changes
    untracked(() => this.propertyOverrides.set({}));
  });

  // Properties that cannot be edited
  private readonly BLOCKED_PROPERTIES = new Set([
    'obid',
    'skey',
    'salias',
    'parent_obid',
    'class_name',
    'crtdt',
    'enddt',
    'oid',
    'object_name',
    'path',
  ]);

  // Technical field keys that should be in the collapsed section
  private readonly technicalFields = new Set([
    'obid',
    'skey',
    'salias',
    'parent_obid',
    'object_name',
    'crtdt',
    'enddt',
    'path',
  ]);

  readonly hasProperties = computed(() => {
    const props = this.asset().metadata.properties;
    if (!props) return false;
    return Object.entries(props).some(
      ([k, v]) => v !== null && !k.startsWith('_'),
    );
  });

  readonly hasRelationships = computed(() => {
    const rel = this.asset().metadata.relationships;
    return Array.isArray(rel) && rel.length > 0;
  });

  private readonly allRelevantProperties = computed(() => {
    const props = this.asset().metadata.properties;
    if (!props) return [];
    const overrides = this.propertyOverrides();
    return Object.entries(props)
      .filter(
        ([k, v]) =>
          (v !== null || k in overrides) &&
          !k.startsWith('_') &&
          !this.technicalFields.has(k),
      )
      .map(([key, value]) => {
        const effectiveValue = key in overrides ? overrides[key] : value;
        return {
          key,
          label: this.formatLabel(key),
          value: this.formatValue(effectiveValue),
          isObject: this.isJsonValue(effectiveValue),
          raw: effectiveValue,
          isEditable: !this.BLOCKED_PROPERTIES.has(key),
        };
      });
  });

  readonly relevantProperties = computed(() =>
    this.allRelevantProperties().filter((p) => !p.isObject),
  );

  readonly jsonProperties = computed(() =>
    this.allRelevantProperties().filter((p) => p.isObject),
  );

  readonly technicalProperties = computed(() => {
    const props = this.asset().metadata.properties;
    if (!props) return [];
    return Object.entries(props)
      .filter(
        ([k, v]) =>
          v !== null && !k.startsWith('_') && this.technicalFields.has(k),
      )
      .map(([key, value]) => ({
        key,
        label: this.formatLabel(key),
        value: this.formatValue(value),
        isObject: this.isJsonValue(value),
        raw: value,
        isEditable: false, // Technical properties are read-only
      }));
  });

  readonly hasTechnicalProperties = computed(
    () => this.technicalProperties().length > 0,
  );

  readonly relationshipNodes = computed((): TreeNode[] => {
    const relationships = this.asset().metadata.relationships;
    if (!relationships?.length) return [];

    const byType = new Map<string, AssetRelationship[]>();
    for (const rel of relationships) {
      const group = byType.get(rel.relationshipType) ?? [];
      group.push(rel);
      byType.set(rel.relationshipType, group);
    }

    return Array.from(byType.entries()).map(([relType, members]) => ({
      value: relType,
      label: this.formatLabel(relType),
      isRelationship: true,
      children: members.map((m) => ({
        value: m.id,
        label: m.name || m.id,
        badge: m.entityType,
      })),
    }));
  });

  onClose(): void {
    this.close.emit();
  }

  onNavigate(assetId: string): void {
    this.navigateToAsset.emit(assetId);
  }

  onCopyJson(): void {
    const asset = this.asset();
    const data = {
      id: asset.id,
      name: asset.name,
      type: asset.type,
      description: asset.metadata.description,
      owner: asset.metadata.owner,
      tags: asset.metadata.tags,
      properties: asset.metadata.properties,
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  }

  toggleStructuredProperties(): void {
    this.structuredPropertiesExpanded.set(!this.structuredPropertiesExpanded());
  }

  toggleTechnicalDetails(): void {
    this.technicalDetailsExpanded.set(!this.technicalDetailsExpanded());
  }

  async onPropertySave(
    event: { propertyName: string; value: string },
    componentIndex: number,
  ): Promise<void> {
    const component = this.editFields()[componentIndex];
    if (!component) return;

    try {
      const response = await firstValueFrom(
        this.apiAdapter.updatePropertyValue(
          this.asset().id,
          event.propertyName,
          event.value,
        ),
      );

      // Complete save first (before overrides trigger recomputation)
      component.completeSave(true);

      // Then update overrides — triggers computed recomputation with new value
      this.propertyOverrides.update((prev) => ({
        ...prev,
        [event.propertyName]: response.newValue,
      }));

      this.propertyUpdated.emit({
        propertyName: event.propertyName,
        value: response.newValue,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update property';
      component.completeSave(false, errorMessage);
    }
  }

  private formatLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, (c) => c.toUpperCase());
  }

  private formatValue(value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (value instanceof Date) return value.toLocaleDateString();
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'string' && this.isJsonString(value)) {
      try {
        return JSON.stringify(
          JSON.parse(this.normalizeJsonString(value)),
          null,
          2,
        );
      } catch {
        return value;
      }
    }
    return String(value);
  }

  private isJsonValue(value: unknown): boolean {
    if (typeof value === 'object' && value !== null) return true;
    if (typeof value === 'string') return this.isJsonString(value);
    return false;
  }

  private normalizeJsonString(value: string): string {
    let str = value.trim();
    if (str.startsWith('"') && str.endsWith('"')) {
      str = str.slice(1, -1);
    }
    str = str.replace(/""/g, '"');
    return str;
  }

  private isJsonString(value: string): boolean {
    const normalized = this.normalizeJsonString(value);
    return (
      (normalized.startsWith('{') && normalized.endsWith('}')) ||
      (normalized.startsWith('[') && normalized.endsWith(']'))
    );
  }
}
